import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Langue supportée par Dinki. Marchés cibles : Afrique de l'Ouest et Centrale.
 *
 * <p>Le français reste la langue par défaut. Les langues locales sont :</p>
 * <ul>
 *   <li>{@code wo} — Wolof (Sénégal)</li>
 *   <li>{@code bm} — Bambara (Mali)</li>
 *   <li>{@code ha} — Haoussa (Niger / Nigeria), ~50% des Nigériens</li>
 *   <li>{@code dje} — Zarma (Niger), 2ᵉ langue nationale, ISO 639-2/3</li>
 * </ul>
 */
export type DinkiLocale = 'fr' | 'en' | 'wo' | 'bm' | 'ha' | 'dje';

export interface LocaleOption {
  code: DinkiLocale;
  label: string;
  flag: string;
}

/**
 * Service de gestion centralisée de la langue de l'application.
 *
 * <p>Responsabilités :</p>
 * <ul>
 *   <li>Charger la langue persistée dans {@code localStorage}</li>
 *   <li>Détecter la langue navigateur en l'absence de préférence</li>
 *   <li>Mettre à jour ngx-translate et l'attribut {@code lang} de la page</li>
 *   <li>Notifier l'application via {@code current$}</li>
 * </ul>
 *
 * <p><b>Note :</b> l'enregistrement des locales Angular ({@code DatePipe},
 * {@code CurrencyPipe}, {@code DecimalPipe}) ne se fait PAS ici mais au niveau
 * module dans {@code app.config.ts} — ce service étant instancié
 * paresseusement, il ne garantit rien avant le premier rendu.</p>
 */
@Injectable({ providedIn: 'root' })
export class LocaleService {
  private translate = inject(TranslateService);
  private http = inject(HttpClient);

  private readonly STORAGE_KEY = 'dinki_locale';
  private readonly DEFAULT: DinkiLocale = 'fr';
  private readonly TOKEN_KEY = 'dinki_token';

  /** Langues supportées affichables dans le sélecteur. */
  readonly available: LocaleOption[] = [
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'ha', label: 'Hausawa', flag: '🇳🇪' },
    { code: 'dje', label: 'Zarma', flag: '🇳🇪' },
    { code: 'wo', label: 'Wolof', flag: '🇸🇳' },
    { code: 'bm', label: 'Bamanankan', flag: '🇲🇱' },
  ];

  /** Langue courante observable. */
  current$ = new BehaviorSubject<DinkiLocale>(this.DEFAULT);

  constructor() {
    // ⚠️ Les locales Angular (DatePipe / CurrencyPipe / DecimalPipe) NE sont
    // plus enregistrées ici : ce service est instancié paresseusement, si bien
    // que les routes qui ne l'injectent pas (espace client public `/c/:token`)
    // rendaient leurs templates avant l'enregistrement → NG0701.
    // registerLocaleData est désormais appelé au niveau module dans
    // app.config.ts, exécuté avant le premier rendu quelle que soit la route.

    // Langues supportées par ngx-translate
    this.translate.addLangs(this.available.map((l) => l.code));
    this.translate.setDefaultLang(this.DEFAULT);

    const resolved = this.resolveInitial();
    this.use(resolved);
  }

  /** Langue actuellement active. */
  get current(): DinkiLocale {
    return this.current$.value;
  }

  /**
   * Bascule l'application sur une nouvelle langue, persiste le choix,
   * et met à jour l'attribut {@code lang} du document.
   */
  use(locale: DinkiLocale): void {
    if (!this.available.find((l) => l.code === locale)) return;
    this.translate.use(locale);
    this.current$.next(locale);
    try {
      localStorage.setItem(this.STORAGE_KEY, locale);
    } catch {
      /* SSR or storage disabled */
    }
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', locale);
    }
    // Synchronisation backend si utilisateur connecté.
    this.persistToBackend(locale);
  }

  /**
   * Pousse la langue choisie vers le backend pour que les notifications
   * asynchrones (SMS / Email cron-driven) utilisent cette langue. Silencieux
   * en cas d'échec (utilisateur non connecté, offline…).
   */
  private persistToBackend(locale: DinkiLocale): void {
    let token: string | null = null;
    try {
      token = localStorage.getItem(this.TOKEN_KEY);
    } catch {
      return;
    }
    if (!token) return;
    this.http
      .put(`${environment.apiUrl}/users/me/locale`, { locale })
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  /**
   * Détermine la langue initiale : choix explicite sauvegardé → français.
   *
   * <p>On n'auto-détecte volontairement PAS la langue du navigateur : le
   * français est la langue par défaut de Dinki. Un utilisateur qui change de
   * langue via le sélecteur voit son choix persisté ({@code localStorage}) et
   * respecté aux visites suivantes.</p>
   */
  private resolveInitial(): DinkiLocale {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY) as DinkiLocale | null;
      if (saved && this.available.find((l) => l.code === saved)) return saved;
    } catch {
      /* ignore */
    }
    return this.DEFAULT;
  }

  /** Libellé de la langue courante pour affichage. */
  currentLabel(): string {
    return this.available.find((l) => l.code === this.current)?.label || 'Français';
  }

  /** Flag (emoji) de la langue courante pour affichage. */
  currentFlag(): string {
    return this.available.find((l) => l.code === this.current)?.flag || '🇫🇷';
  }
}
